import React from 'react';
import { Link } from 'react-router-dom';
import { User, Calendar, AlertTriangle, ExternalLink, BadgeCheck } from 'lucide-react';

// Real publishing organizations behind the cited studies → homepage + favicon.
const ORG_DIRECTORY = {
  'National Library of Medicine (PubMed)': { url: 'https://pubmed.ncbi.nlm.nih.gov/', domain: 'pubmed.ncbi.nlm.nih.gov' },
  'ClinicalTrials.gov (NIH)': { url: 'https://clinicaltrials.gov/', domain: 'clinicaltrials.gov' },
  'National Institutes of Health': { url: 'https://www.nih.gov/', domain: 'nih.gov' },
  'JAMA Network': { url: 'https://jamanetwork.com/', domain: 'jamanetwork.com' },
  'Nature Portfolio': { url: 'https://www.nature.com/', domain: 'nature.com' },
  'American Chemical Society': { url: 'https://pubs.acs.org/', domain: 'pubs.acs.org' },
  'PNAS': { url: 'https://www.pnas.org/', domain: 'pnas.org' },
  'ScienceDirect (Elsevier)': { url: 'https://www.sciencedirect.com/', domain: 'sciencedirect.com' },
  'Springer Nature': { url: 'https://link.springer.com/', domain: 'link.springer.com' },
  'Wiley Online Library': { url: 'https://onlinelibrary.wiley.com/', domain: 'onlinelibrary.wiley.com' },
  'Oxford University Press': { url: 'https://academic.oup.com/', domain: 'academic.oup.com' },
  'The Lancet': { url: 'https://www.thelancet.com/', domain: 'thelancet.com' },
  'New England Journal of Medicine': { url: 'https://www.nejm.org/', domain: 'nejm.org' },
  'Cell Press': { url: 'https://www.cell.com/', domain: 'cell.com' },
  'Frontiers': { url: 'https://www.frontiersin.org/', domain: 'frontiersin.org' },
  'MDPI': { url: 'https://www.mdpi.com/', domain: 'mdpi.com' },
  'Aging (Impact Journals)': { url: 'https://www.aging-us.com/', domain: 'aging-us.com' },
  'WHO / IARC': { url: 'https://www.iarc.who.int/', domain: 'iarc.who.int' },
  'World Health Organization': { url: 'https://www.who.int/', domain: 'who.int' },
  'Centers for Disease Control and Prevention': { url: 'https://www.cdc.gov/', domain: 'cdc.gov' },
  'U.S. Food & Drug Administration': { url: 'https://www.fda.gov/', domain: 'fda.gov' },
  'Mayo Clinic': { url: 'https://www.mayoclinic.org/', domain: 'mayoclinic.org' },
  'Cleveland Clinic': { url: 'https://my.clevelandclinic.org/', domain: 'my.clevelandclinic.org' },
  'Harvard T.H. Chan School of Public Health': { url: 'https://hsph.harvard.edu/', domain: 'hsph.harvard.edu' },
  'Harvard University': { url: 'https://www.harvard.edu/', domain: 'harvard.edu' },
  'Johns Hopkins Bloomberg School of Public Health': { url: 'https://publichealth.jhu.edu/', domain: 'publichealth.jhu.edu' },
  'Johns Hopkins University': { url: 'https://www.jhu.edu/', domain: 'jhu.edu' },
  'World Anti-Doping Agency': { url: 'https://www.wada-ama.org/', domain: 'wada-ama.org' },
  'Examine.com': { url: 'https://examine.com/', domain: 'examine.com' },
  'Global Wellness Institute': { url: 'https://globalwellnessinstitute.org/', domain: 'globalwellnessinstitute.org' },
  'American Heart Association': { url: 'https://www.ahajournals.org/', domain: 'ahajournals.org' },
  'American Diabetes Association': { url: 'https://diabetesjournals.org/', domain: 'diabetesjournals.org' },
  'Endocrine Society': { url: 'https://www.endocrine.org/', domain: 'endocrine.org' },
};

/**
 * EvidenceOrganizations — compact "Sources & Studies" trust anchor.
 * Lists the real organizations that published the studies this YMYL
 * article cites, each with its favicon (logo) and a link. Reinforces
 * E-E-A-T for Google + AI Overviews.
 */
export function EvidenceOrganizations({ sources }) {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  return (
    <section className="mt-12 pt-8 border-t border-zinc-800" data-testid="evidence-organizations">
      <div className="flex items-center gap-2 mb-2">
        <BadgeCheck className="w-4 h-4 text-[#C7FA3E]" />
        <h2 className="text-white text-lg font-heading uppercase tracking-wider">Sources &amp; Studies</h2>
      </div>
      <p className="text-zinc-500 text-xs mb-5">
        This article is grounded in peer-reviewed research published by the organizations below.
      </p>
      <div className="flex flex-wrap gap-3">
        {sources.map((name) => {
          const org = ORG_DIRECTORY[name];
          const favicon = org ? `https://www.google.com/s2/favicons?domain=${org.domain}&sz=64` : null;
          const content = (
            <>
              {favicon ? (
                <img src={favicon} alt="" width={20} height={20} className="w-5 h-5 rounded-sm bg-white/90 p-0.5" loading="lazy" />
              ) : (
                <BadgeCheck className="w-5 h-5 text-[#C7FA3E]" />
              )}
              <span className="text-zinc-200 text-sm">{name}</span>
              {org && <ExternalLink className="w-3 h-3 text-zinc-500" />}
            </>
          );
          const cls = 'inline-flex items-center gap-2.5 px-3.5 py-2 bg-zinc-900/60 border border-zinc-800 hover:border-[#C7FA3E]/40 transition-colors';
          return org ? (
            <a
              key={name}
              href={org.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={cls}
              data-testid={`evidence-org-${org.domain}`}
            >
              {content}
            </a>
          ) : (
            <span key={name} className={cls} data-testid="evidence-org-plain">{content}</span>
          );
        })}
      </div>
    </section>
  );
}

/**
 * AuthorByline — Shows author card with credentials + last reviewed date.
 * Rendered at the top of every article.
 */
export function AuthorByline({ article }) {
  // "Last reviewed" only with an explicit review record — otherwise the date
  // shown is the honest content-update date, never a fabricated review stamp.
  const hasReviewDate = Boolean(article?.last_reviewed);
  const reviewDate = article?.last_reviewed || article?.updated_at || article?.created_at;
  const dateLabel = hasReviewDate ? 'Last reviewed' : 'Updated';
  const formattedDate = reviewDate ? new Date(reviewDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
  const reviewerName = article?.reviewed_by;
  const author = article?.author || 'NinjAthlete Team';
  const isNamedExpert = author !== 'NinjAthlete Team';
  // Generate initials from named author (e.g. "Dr. Trevor Bachmeyer" -> "TB")
  const initials = isNamedExpert
    ? author.replace(/^Dr\.?\s+/i, '').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : 'NA';

  return (
    <div className="flex items-center gap-3 py-3 mb-6 border-b border-zinc-800/50" data-testid="author-byline">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#1D3557] flex items-center justify-center text-white text-[10px] font-bold shrink-0">
        {initials}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="text-zinc-300 font-medium">{author}</span>
        {reviewerName && reviewerName !== author && (
          <>
            <span className="text-zinc-700">|</span>
            <span className="text-zinc-400">Evidence from {reviewerName}</span>
          </>
        )}
        {formattedDate && (
          <>
            <span className="text-zinc-700">|</span>
            <span className="text-zinc-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {dateLabel}: {formattedDate}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * MedicalDisclaimer — Shown on peptide/YMYL articles.
 * Compact, non-intrusive, but meets E-E-A-T requirements.
 */
export function MedicalDisclaimer({ compact = false }) {
  if (compact) {
    return (
      <p className="text-zinc-600 text-[10px] mt-6 pt-3 border-t border-zinc-800/50" data-testid="medical-disclaimer-compact">
        <AlertTriangle className="w-3 h-3 inline mr-1 text-zinc-600" />
        For educational purposes only — not medical advice. Consult a healthcare professional before starting any peptide protocol.
        {' '}<Link to="/editorial-policy" className="underline hover:text-zinc-400">Editorial policy</Link>
      </p>
    );
  }

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/50 px-4 py-3 mb-6" data-testid="medical-disclaimer">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-500/70 mt-0.5 shrink-0" />
        <div className="text-xs text-zinc-500 leading-relaxed">
          <p className="mb-1">
            <strong className="text-zinc-400">Medical Disclaimer:</strong> This article is for educational and informational purposes only. It is not intended as medical advice, diagnosis, or treatment.
          </p>
          <p>
            Always consult a qualified healthcare professional before starting any peptide protocol, supplement regimen, or training program.
            Sources are cited with DOI/PubMed links where available.
            {' '}<Link to="/editorial-policy" className="text-zinc-400 underline hover:text-white transition-colors">Read our editorial policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * DirectAnswerBlock — The AEO/GEO "Direct Answer Pattern" wrapper.
 * Wraps the first answer paragraph and key points for featured snippet capture.
 */
export function DirectAnswerBlock({ children }) {
  return (
    <div className="direct-answer mb-6" data-testid="direct-answer-block">
      {children}
    </div>
  );
}

/**
 * SourcesAndReferences — E-E-A-T citation block attributing the real
 * PubMed studies (and their authors) that the article is derived from.
 */
export function SourcesAndReferences({ citations }) {
  if (!Array.isArray(citations) || citations.length === 0) return null;
  return (
    <section className="mt-12 pt-8 border-t border-zinc-800" data-testid="sources-references">
      <h2 className="text-white text-lg font-heading uppercase tracking-wider mb-2">
        Sources &amp; References
      </h2>
      <p className="text-zinc-500 text-xs mb-5">
        Peer-reviewed research this article draws on, attributed to the original study authors and verified against PubMed.
      </p>
      <ol className="space-y-4">
        {citations.map((c, i) => {
          const authors = Array.isArray(c.authors) ? c.authors : [];
          const shownAuthors = authors.slice(0, 3).join(', ') + (authors.length > 3 ? ', et al.' : '');
          return (
            <li key={c.pmid || i} className="flex gap-3 text-sm" data-testid={`citation-${i}`}>
              <span className="text-[#C7FA3E] font-mono text-xs mt-0.5 shrink-0">[{i + 1}]</span>
              <div className="leading-relaxed">
                {shownAuthors && <span className="text-zinc-300">{shownAuthors} </span>}
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-white underline decoration-zinc-700 hover:decoration-[#C7FA3E] transition-colors"
                  data-testid={`citation-link-${i}`}
                >
                  {c.title}
                  <ExternalLink className="w-3 h-3 inline ml-1 mb-0.5" />
                </a>
                <span className="text-zinc-600 text-xs block mt-0.5">
                  {c.journal}{c.year ? ` (${c.year})` : ''} · PMID: {c.pmid}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
